export default class VideoPlayerBrightcove {
  static playerLibraryRequested = false;

  static playerLibraryLoaded = false;

  static playerReadyCallbacks = [];

  constructor(src, container) {
    this.rawSrc = src;
    this.container = container;

    this.loadPlayerLibrary().then(this.loadVideo.bind(this));
  }

  // Injects and loads Brightcove library
  loadPlayerLibrary() {
    return new Promise((resolve) => {
      const proto = Object.getPrototypeOf(this);
      const thisClass = proto.constructor;

      if (thisClass.playerLibraryLoaded) {
        return resolve();
      }

      thisClass.playerReadyCallbacks.push(resolve);

      if (!this.playerLibraryRequested) {
        this.playerLibraryRequested = true;

        // Inject library
        const script = document.createElement("script");
        const stylelink = document.createElement("link");

        script.src = "https://vjs.zencdn.net/7.10.2/video.js";
        stylelink.rel = "stylesheet";
        stylelink.href = "https://vjs.zencdn.net/7.10.2/video-js.css";

        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
        firstScriptTag.parentNode.insertBefore(stylelink, firstScriptTag);

        thisClass.checkPlayerLoaded();
      }
    });
  }

  loadVideo() {
    [, this.account, this.player, this.videoID] = this.extractIdFromURL(
      this.rawSrc
    );

    if (this.videoID) {
      this.renderPlayer();
    }
  }

  extractIdFromURL(rawVideoURL = "") {
    const match = rawVideoURL.match(
      /players\.brightcove\.net\/([0-9]+)\/([^_]+)_.+\/.+videoId=([0-9]+)/
    );
    return match || null;
  }

  renderPlayer() {
    const video = document.createElement("video");
    const script = document.createElement("script");
    const id = `video-js_${this.videoID}_${
      Date.now() * Math.round(Math.random())
    }`;

    video.setAttribute("data-video-id", this.videoID);
    video.setAttribute("data-player", this.player);
    video.setAttribute("data-account", this.account);
    video.setAttribute("data-embed", "default");
    video.setAttribute("class", "video-js");
    video.setAttribute("id", id);
    video.setAttribute("controls", true);

    script.setAttribute(
      "src",
      `https://players.brightcove.net/${this.account}/${this.player}_default/index.min.js?controls=true`
    );

    // Append to DOM
    this.container.appendChild(video);
    this.container.appendChild(script);

    this.awaitPlayerReady(id, (player) => {
      this.rawPlayer = player;
      this.onPlayerReady();
    });
  }

  awaitPlayerReady(playerID, callback) {
    window.requestAnimationFrame(() => {
      const player = window.videojs.getPlayer(playerID);

      if (player) {
        player.ready(() => callback(player));
      } else {
        this.awaitPlayerReady(playerID, callback);
      }
    });
  }

  onPlayerReady() {
    // Update poster image on load
    const obs = new MutationObserver((mutations) => {
      [...mutations].some((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "poster"
        ) {
          const picture =
            this.container.nextElementSibling.firstElementChild ||
            Object.assign(document.createElement("picture"), {
              className: "responsive-image",
            });

          picture.innerHTML = `
            <source media="(max-width: 767px)" srcset="${mutation.target.poster.replace(
              /[^/]+\/match/,
              "800x450/match"
            )}">
            <source media="(max-width: 1199px)" srcset="${mutation.target.poster.replace(
              /[^/]+\/match/,
              "1024x768/match"
            )}">
            <source media="(min-width: 1200px)" srcset="${mutation.target.poster.replace(
              /[^/]+\/match/,
              "1200x800/match"
            )}">
            <img src="${mutation.target.poster.replace(
              /[^/]+\/match/,
              "800x450/match"
            )}">
          `;

          this.container.nextElementSibling.innerHTML = picture.outerHTML;
          obs.disconnect();

          window.pageYOffset = window.scrollY; // Just to force layout
          this.container.nextElementSibling.classList.add("image-loaded");

          return true;
        }

        return false;
      });
    });

    obs.observe(this.container, { attributes: true, subtree: true });
  }

  play() {
    this.rawPlayer.ready(() => {
      this.rawPlayer.play();
    });
  }

  static checkPlayerLoaded() {
    if (window.videojs && window.videojs) {
      // Run callbacks
      this.playerReadyCallbacks.forEach((callback) => callback());
    }

    // Retry check
    window.requestAnimationFrame(() => this.checkPlayerLoaded());
  }
}
